/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { forwardRef, useEffect, useRef } from 'react';

import { Section } from './Section';
import { actions, useAppDispatch, useAppSelector } from './store';
import { useMergedRef } from './useMergedRef';
import {
  selectAnchorSectionIds,
  selectAnchorSectionIsAnimating,
} from './store/selectors';

import type { ComponentPropsWithRef } from 'react';
import type { NavigateOptions } from '@reach/router';

export type UseAnchorConfig<TState> = NavigateOptions<TState>;

/**
 * A hook that will update the router hash location to match the id
 * of the referenced element whenever the element is scrolled past.
 */
export function useAnchor<T extends HTMLElement, TState>({
  replace = true,
  state,
}: UseAnchorConfig<TState> = {}): (node: T | null) => void {
  const dispatch = useAppDispatch();

  const isAnimating = useAppSelector(selectAnchorSectionIsAnimating);
  const sectionIds = useAppSelector(selectAnchorSectionIds);

  const ref = useRef<HTMLElement | null>(null);

  const optionsRef = useRef({ replace, state });
  optionsRef.current = { replace, state };

  const sectionIdsRef = useRef(sectionIds);
  sectionIdsRef.current = sectionIds;

  useEffect(() => {
    const id = ref.current?.id ?? null;
    if (id === null) return;

    dispatch(
      actions.anchorSections.sectionAdd({ id, options: { replace, state } }),
    );

    return () => {
      dispatch(actions.anchorSections.sectionRemove({ id }));
    };
  }, [dispatch, isAnimating, replace, state]);

  return useMergedRef(ref);
}

export interface AnchorSectionProps<TState>
  extends ComponentPropsWithRef<typeof Section>,
    UseAnchorConfig<TState> {
  id: string;
}

/**
 * A `Section` that binds its `id` to the location hash.
 */
export const AnchorSection = forwardRef(function AnchorSection(
  { replace = true, state, ...props },
  forwardedRef,
): React.JSX.Element {
  const mergedRef = useMergedRef(
    forwardedRef,
    useAnchor({
      replace,
      state,
    }),
  );

  return <Section ref={mergedRef} {...props} />;
}) as <TState>(props: AnchorSectionProps<TState>) => JSX.Element;
// NOTE: We assert the above component as a function type (instead of
// defining it in the `forwardRef` call or function declaration)
// because the `forwardRef` type does not support generics
// (like `<TState>`) in prop types.
// See https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref/58473012
